<template>
  <div class="shell">
    <aside
      :class="['sidebar', { collapsed: !sidebarOpen }]"
      :aria-label="t('shell_sidebarAriaLabel')"
    >
      <div class="sidebar-top">
        <span class="sidebar-logo">PunchLog</span>
        <span class="sidebar-mini-logo">PL</span>
        <span class="sidebar-tagline">{{ t('shell_tagline') }}</span>
      </div>
      <nav class="sidebar-nav" :aria-label="t('shell_navAriaLabel')">
        <router-link
          v-for="tab in tabs"
          :key="tab.to"
          :to="tab.to"
          class="sidebar-link"
          :aria-label="tab.label"
        >
          <SvgIcon :name="tab.icon" :size="18" />
          <span class="sidebar-link-label">{{ tab.label }}</span>
        </router-link>
      </nav>
      <div class="sidebar-footer">
        <div class="sidebar-footer-row">
          <div class="sidebar-status">
            <span :class="['net-dot', network.connected ? 'online' : 'offline']" />
            <span class="net-label">{{
              network.connected ? t('shell_online') : t('shell_offline')
            }}</span>
          </div>
          <button
            class="theme-btn"
            :aria-label="dark ? t('shell_switchToLight') : t('shell_switchToDark')"
            @click="toggleTheme"
          >
            <SvgIcon :name="dark ? 'sun' : 'moon'" :size="16" />
          </button>
          <button
            class="theme-btn"
            :aria-label="locale === 'en' ? 'Cambiar a español' : 'Switch to English'"
            @click="setLocale(locale === 'en' ? 'es' : 'en')"
          >
            {{ locale === 'en' ? 'ES' : 'EN' }}
          </button>
        </div>
        <span class="version">v{{ appVersion }}</span>
      </div>
    </aside>

    <div class="shell-body">
      <header class="top-bar">
        <div class="top-bar-left">
          <span class="mobile-logo">PL</span>
          <button
            class="sidebar-toggle desktop-only"
            :title="sidebarOpen ? t('shell_collapseSidebar') : t('shell_expandSidebar')"
            :aria-label="sidebarOpen ? t('shell_collapseSidebar') : t('shell_expandSidebar')"
            @click="sidebarOpen = !sidebarOpen"
          >
            <SvgIcon name="menu" :size="16" />
          </button>
          <span class="breadcrumb">{{ currentLabel }}</span>
        </div>
        <div class="top-bar-right">
          <span v-if="lastSync" class="sync-ts">{{
            t('shell_syncedAgo', { n: lastSyncDisplay })
          }}</span>
          <span v-if="activeSession" class="session-pill" aria-live="polite">
            <span class="session-dot" />{{ sessionElapsed }}
          </span>
          <span v-if="!network.connected" class="net-pill offline">{{ t('shell_offline') }}</span>
          <NotificationBell />
          <button
            class="theme-btn mobile-only"
            :aria-label="dark ? t('shell_switchToLight') : t('shell_switchToDark')"
            @click="toggleTheme"
          >
            <SvgIcon :name="dark ? 'sun' : 'moon'" :size="18" />
          </button>
          <button
            class="theme-btn mobile-only"
            :aria-label="locale === 'en' ? 'Cambiar a español' : 'Switch to English'"
            @click="setLocale(locale === 'en' ? 'es' : 'en')"
          >
            {{ locale === 'en' ? 'ES' : 'EN' }}
          </button>
        </div>
      </header>

      <div v-if="isPastDue && !dunningDismissed" class="banner banner-danger dunning-banner">
        <SvgIcon name="alert" :size="16" />
        <span>{{ t('shell_dunningMessage') }}</span>
        <button class="banner-cta" @click="openPortal">
          {{ t('shell_dunningAction') }}
        </button>
        <button class="banner-cta" @click="dismissDunning">
          {{ t('shell_dunningDismiss') }}
        </button>
      </div>

      <main class="shell-content">
        <router-view />
      </main>

      <nav class="tab-bar" :aria-label="t('shell_navAriaLabel')">
        <router-link
          v-for="tab in tabs"
          :key="tab.to"
          :to="tab.to"
          class="tab"
          :aria-label="tab.label"
        >
          <SvgIcon :name="tab.icon" :size="22" />
          <span class="tab-label">{{ tab.label }}</span>
        </router-link>
      </nav>
    </div>
  </div>

  <Teleport to="body">
    <div class="toast-stack" aria-live="polite">
      <TransitionGroup name="toast">
        <button
          v-for="toast in toasts"
          :key="toast.id"
          :class="['toast', `toast--${toast.variant}`]"
          @click="dismiss(toast.id)"
        >
          <SvgIcon
            :name="
              toast.variant === 'success' ? 'check' : toast.variant === 'error' ? 'alert' : 'cloud'
            "
            :size="16"
            class="toast-icon"
          />
          <span class="toast-msg">{{ toast.message }}</span>
          <button v-if="toast.action" class="toast-action" @click.stop="toast.action.fn">
            {{ toast.action.label }}
          </button>
        </button>
      </TransitionGroup>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { useRoute } from 'vue-router';
import { Network } from '@capacitor/network';
import { useStore } from 'vue-livestore';
import SvgIcon from '@/ui/components/SvgIcon.vue';
import NotificationBell from '@/ui/components/NotificationBell.vue';
import { useToast } from '@/core/useToast.js';
import { useNotifications } from '@/core/useNotifications.js';
import { activeSession } from '@/core/sessionStore.js';
import { useI18n } from 'vue-i18n';
import { useLocale } from '@/core/useLocale.js';
import { initDriveSync } from '@/features/sync/useDriveSync.js';
import { useSubscription } from '@/features/subscription/useSubscription.js';
import { useTeamSchedule } from '@/features/team/useTeamSchedule.js';
import { purgeExpiredWorkplaces } from '@/features/workplaces/purgeExpiredWorkplaces.js';
import { purgeExpiredScheduleTemplates } from '@/features/schedules/purgeExpiredScheduleTemplates.js';
const { store } = useStore();
initDriveSync(store);

const { t } = useI18n();
const { locale, setLocale } = useLocale();
const { dismiss, toasts } = useToast();
const { openPortal, status } = useSubscription();
const notifications = useNotifications();

const props = defineProps({
  lastSync: { default: null, type: String },
  tier: { default: 'free', type: String },
});

const network = ref({ connected: true });
const dark = ref(false);
const sidebarOpen = ref(true);
const route = useRoute();
const now = ref(Date.now());
const dunningDismissed = ref(sessionStorage.getItem('dunning_dismissed') === '1');
let ticker;

const isPastDue = computed(() => (status as { value?: string })?.value === 'past_due');

// Persistent notification when subscription goes past due
watch(isPastDue, (val, old) => {
  if (val && !old) {
    const { hasUnresolved } = notifications;
    if (!hasUnresolved(t('notif_pastDueTitle'))) {
      notifications.notify({
        actionTab: '/settings/plan',
        body: t('notif_pastDueBody'),
        title: t('notif_pastDueTitle'),
        type: 'warning',
      });
    }
  }
});

const sessionElapsed = computed(() => {
  if (!activeSession.value) return '';
  const ms = now.value - new Date(activeSession.value.clockIn).getTime();
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  const s = Math.floor((ms % 60000) / 1000);
  return h > 0
    ? `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
    : `${m}:${String(s).padStart(2, '0')}`;
});

const tabs = computed(() => [
  { icon: 'shift', label: t('shell_tabTrack'), to: '/' },
  { icon: 'calendar', label: t('shell_tabPlanner'), to: '/planner' },
  { icon: 'scan', label: t('shell_tabScan'), to: '/ocr' },
  { icon: 'document', label: t('shell_tabReports'), to: '/reports' },
  { icon: 'settings', label: t('shell_tabSettings'), to: '/settings' },
]);

const currentLabel = computed(
  () => tabs.value.find(tab => tab.to === route.path)?.label ?? 'PunchLog',
);

const appVersion = __APP_VERSION__;

const lastSyncDisplay = computed(() => {
  if (!props.lastSync) return null;
  const diff = Math.floor((Date.now() - new Date(props.lastSync).getTime()) / 60000);
  if (diff < 1) return t('shell_syncedJustNow');
  if (diff < 60) return t('shell_syncedMinutes', { n: diff });
  return t('shell_syncedHours', { n: Math.floor(diff / 60) });
});

function applyTheme(isDark) {
  document.documentElement.classList.toggle('dark', isDark);
  document.documentElement.classList.toggle('light', !isDark);
  localStorage.setItem('theme', isDark ? 'dark' : 'light');
  dark.value = isDark;
}
function toggleTheme() {
  applyTheme(!dark.value);
}
function dismissDunning() {
  dunningDismissed.value = true;
  sessionStorage.setItem('dunning_dismissed', '1');
}

onMounted(async () => {
  const saved = localStorage.getItem('theme');
  const sysDark = globalThis.matchMedia('(prefers-color-scheme: dark)').matches;
  applyTheme(saved ? saved === 'dark' : sysDark);
  const status = await Network.getStatus();
  network.value = status;
  Network.addListener('networkStatusChange', s => {
    network.value = s;
  });
  ticker = setInterval(() => {
    now.value = Date.now();
  }, 1000);
  const { pullAssignments } = useTeamSchedule();
  pullAssignments();
  purgeExpiredWorkplaces(store);
  purgeExpiredScheduleTemplates(store);
});
onUnmounted(() => {
  Network.removeAllListeners();
  clearInterval(ticker);
});
</script>

<style scoped>
/* ── Base shell ── */
.shell {
  display: flex;
  height: 100%;
  overflow: hidden;
}
.shell-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  overflow: hidden;
}
.shell-content {
  flex: 1;
  overflow-y: auto;
}

/* ── Top bar ── */
.top-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 48px;
  padding: 0 16px;
  background: var(--surface);
  border-bottom: 1px solid var(--border);
  flex-shrink: 0;
  position: sticky;
  top: 0;
  z-index: 10;
}
.top-bar-left {
  display: flex;
  align-items: center;
  gap: 10px;
}
.top-bar-right {
  display: flex;
  align-items: center;
  gap: 8px;
}
.breadcrumb {
  font-size: var(--text-base);
  font-weight: 600;
  color: var(--text);
}
.mobile-logo {
  font-size: 1.1rem;
  font-weight: 700;
  letter-spacing: -0.5px;
}
.sync-ts {
  font-size: var(--text-xs);
  color: var(--muted);
}

.net-pill {
  font-size: var(--text-xs);
  padding: 2px 8px;
  border-radius: 0;
  font-weight: 500;
}
.net-pill.offline {
  background: var(--clr-danger-bg);
  color: var(--clr-danger);
  animation: pulse 1.5s ease-in-out infinite;
}

.session-pill {
  display: flex;
  align-items: center;
  gap: 5px;
  background: var(--clr-success-bg);
  color: var(--clr-success);
  font-size: var(--text-xs);
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  padding: 3px 10px;
  border-radius: 0;
  letter-spacing: 0.01em;
}
.session-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--clr-success);
  flex-shrink: 0;
  animation: pulse 1.5s ease-in-out infinite;
}

.theme-btn,
.sidebar-toggle {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 0;
  border: 1px solid var(--border);
  background: var(--surface-raised);
  color: var(--muted);
  cursor: pointer;
}
.theme-btn:hover,
.sidebar-toggle:hover {
  color: var(--text);
}
.sidebar-toggle {
  display: none;
}

/* ── Sidebar — hidden on mobile ── */
.sidebar {
  display: none;
}

/* ── Mobile bottom tab bar ── */
.tab-bar {
  display: flex;
  border-top: 1px solid var(--border);
  background: var(--surface);
  padding-bottom: env(safe-area-inset-bottom, 0);
  flex-shrink: 0;
}
.tab {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 48px;
  padding: 8px 4px 6px;
  text-decoration: none;
  color: var(--muted);
  font-size: var(--text-xs);
  transition: color 0.15s;
}
.tab.router-link-active {
  color: var(--clr-accent);
}
.tab-label {
  margin-top: 3px;
}

/* ── Desktop ── */
@media (min-width: 768px) {
  .mobile-logo {
    display: none;
  }
  .mobile-only {
    display: none !important;
  }
  .tab-bar {
    display: none;
  }

  .sidebar {
    display: flex;
    flex-direction: column;
    width: 240px;
    flex-shrink: 0;
    background: var(--surface);
    border-right: 1px solid var(--border);
    height: 100%;
    overflow-y: auto;
    overflow-x: hidden;
    transition: width 0.2s ease;
  }
  .sidebar.collapsed {
    width: 52px;
  }

  .sidebar-toggle {
    display: flex;
  }
  .desktop-only {
    display: flex !important;
  }

  /* ── Expanded top ── */
  .sidebar-top {
    padding: 20px 16px 12px;
    border-bottom: 1px solid var(--border);
  }
  .sidebar-logo {
    display: block;
    font-size: 1.1rem;
    font-weight: 700;
    letter-spacing: -0.5px;
  }
  .sidebar-mini-logo {
    display: none;
    font-size: 1rem;
    font-weight: 700;
    letter-spacing: -0.5px;
    text-align: center;
  }
  .sidebar-tagline {
    display: block;
    font-size: var(--text-xs);
    color: var(--muted);
    margin-top: 2px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  /* ── Collapsed top: show PL, hide full logo + tagline ── */
  .collapsed .sidebar-top {
    padding: 16px 0;
    display: flex;
    justify-content: center;
  }
  .collapsed .sidebar-logo {
    display: none;
  }
  .collapsed .sidebar-mini-logo {
    display: block;
  }
  .collapsed .sidebar-tagline {
    display: none;
  }

  /* ── Nav ── */
  .sidebar-nav {
    flex: 1;
    padding: 10px 8px;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }
  .collapsed .sidebar-nav {
    padding: 10px 0;
    align-items: center;
  }

  .sidebar-link {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 9px 12px;
    border-radius: 0;
    text-decoration: none;
    color: var(--muted);
    font-size: var(--text-base);
    font-weight: 500;
    transition:
      background 0.12s,
      color 0.12s;
  }
  .sidebar-link:hover {
    background: var(--surface-raised);
    color: var(--text);
  }
  .sidebar-link.router-link-active {
    background: var(--clr-accent-bg);
    color: var(--clr-accent);
    font-weight: 600;
  }
  .sidebar-link-label {
    white-space: nowrap;
  }

  .collapsed .sidebar-link {
    padding: 10px;
    justify-content: center;
    width: 100%;
  }
  .collapsed .sidebar-link-label {
    display: none;
  }

  /* ── Footer ── */
  .sidebar-footer {
    padding: 10px 16px 8px;
    border-top: 1px solid var(--border);
    display: flex;
    flex-direction: column;
    gap: 2px;
  }
  .sidebar-footer-row {
    display: flex;
    align-items: center;
    gap: 6px;
  }
  .sidebar-status {
    display: flex;
    align-items: center;
    gap: 5px;
    flex: 1;
    min-width: 0;
  }
  .net-dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    flex-shrink: 0;
  }
  .net-dot.online {
    background: var(--clr-success);
  }
  .net-dot.offline {
    background: var(--clr-danger);
    animation: pulse 1.5s ease-in-out infinite;
  }
  .net-label {
    font-size: var(--text-sm);
    color: var(--muted);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .version {
    font-size: var(--text-xs);
    color: var(--muted);
    padding-left: 12px;
  }

  /* ── Collapsed footer: only the status dot ── */
  .collapsed .sidebar-footer {
    padding: 12px 0;
    align-items: center;
  }
  .collapsed .sidebar-footer-row {
    justify-content: center;
  }
  .collapsed .net-label,
  .collapsed .theme-btn,
  .collapsed .version {
    display: none;
  }
  .collapsed .sidebar-status {
    flex: unset;
  }
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

/* ── Global toast stack ── */
.toast-stack {
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 9999;
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: center;
  pointer-events: none;
}

.toast {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  border-radius: 8px;
  min-width: 240px;
  max-width: min(90vw, 400px);
  background: #1e2025;
  color: #fff;
  font-size: 13px;
  font-weight: 500;
  box-shadow:
    0 4px 16px rgba(0, 0, 0, 0.25),
    0 1px 4px rgba(0, 0, 0, 0.15);
  cursor: pointer;
  pointer-events: all;
  border: none;
  text-align: left;
  font-family: inherit;
}

.toast--success .toast-icon {
  color: #4ade80;
}
.toast--error .toast-icon {
  color: #f87171;
}
.toast--info .toast-icon {
  color: #60a5fa;
}

.toast-msg {
  flex: 1;
}

.toast-action {
  background: none;
  border: none;
  color: #60a5fa;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  padding: 0;
  white-space: nowrap;
}

.toast-enter-from {
  opacity: 0;
  transform: translateY(12px);
}
.toast-leave-to {
  opacity: 0;
  transform: translateY(12px);
}
.toast-enter-active,
.toast-leave-active {
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}

.dunning-banner {
  flex-shrink: 0;
  border-radius: 0;
  margin: 0;
}
</style>
